import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Star, TrendingUp, TrendingDown } from 'lucide-react';
import { MarketData } from '../hooks/useDatabase';
import { useFavorites } from '../hooks/useFavorites';
import { TradingMode } from '../App';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from '../constants/tradingPairs';

interface MarketsProps {
  marketData: MarketData[];
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  tradingMode?: TradingMode;
}

const Markets: React.FC<MarketsProps> = ({
  marketData,
  selectedPair,
  setSelectedPair,
  currentPrice,
  tradingMode
}) => {
  const { t } = useTranslation();
  // Log marketData prop as soon as component receives it
  console.log('Markets.tsx - Received marketData prop:', marketData);
  console.log('Markets.tsx - marketData length:', marketData.length);
  console.log('Markets.tsx - Commodity symbols in marketData prop:', 
    marketData.filter(d => d.symbol.startsWith('X') || d.symbol.includes('USD')).map(d => ({ symbol: d.symbol, price: d.price }))
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const { favorites, isFavorite, toggleFavorite, loading: favoritesLoading } = useFavorites();

  // Determine instrument type based on symbol
  const getInstrumentType = useCallback((symbol: string): string => {
    if (tradingMode === 'cfd') {
      // For CFD mode, look up the instrument in CFD_INSTRUMENTS
      const instrument = CFD_INSTRUMENTS.find(item => item.symbol === symbol);
      if (instrument) {
        return instrument.type;
      }
    }
    
    // For non-CFD modes, check if it's crypto
    if (symbol.endsWith('USDT')) {
      return 'crypto';
    }
    
    // Default fallback
    return 'unknown';
  }, [tradingMode]);

  const formatPairDisplay = (symbol: string) => {
    // Handle different asset types based on trading mode
    if (tradingMode === 'cfd') {
      // For CFD instruments - display with slashes for UI only
      if (symbol.includes('USD') && !symbol.includes('USDT')) {
        // For forex pairs, format as XXX/YYY
        if (symbol.length === 6) {
          return `${symbol.substring(0, 3)}/${symbol.substring(3, 6)}`;
        }
        
        // For commodities, use their display names
        if (symbol === 'XAUUSD') return 'Gold/USD';
        if (symbol === 'XAGUSD') return 'Silver/USD';
        if (symbol === 'XPTUSD') return 'Platinum/USD';
        if (symbol === 'WTIUSD') return 'Oil/USD';
      }
      // Stock symbols remain as is
      return symbol;
    }
    
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) {
      return `${(volume / 1e9).toFixed(1)}B`;
    } else if (volume >= 1e6) {
      return `${(volume / 1e6).toFixed(1)}M`;
    } else if (volume >= 1e3) {
      return `${(volume / 1e3).toFixed(1)}K`;
    }
    return volume.toFixed(2);
  };

  // Get filtered market data based on predefined lists, search term, and sort by market cap
  const getFilteredMarketData = () => {
    console.log('Markets.tsx - getFilteredMarketData called');
    console.log('Markets.tsx - tradingMode:', tradingMode);
    console.log('Markets.tsx - marketData in getFilteredMarketData:', marketData.length, 'entries');
    
    // Use the appropriate predefined list based on trading mode
    const predefinedList = tradingMode === 'cfd' ? CFD_INSTRUMENTS : TOP_CRYPTO_PAIRS;
    console.log('Markets.tsx - predefinedList:', predefinedList.map(p => p.symbol));
    
    // Create a map of predefined symbols for quick lookup
    const predefinedSymbols = new Set(predefinedList.map(item => item.symbol));
    console.log('Markets.tsx - predefinedSymbols Set:', Array.from(predefinedSymbols));
    
    // Filter market data to only include predefined symbols
    let filtered = marketData.filter(data => {
      // For both crypto and CFD modes, only show predefined symbols
      return predefinedSymbols.has(data.symbol);
    });
    console.log('Markets.tsx - After predefined filter, filtered length:', filtered.length);
    console.log('Markets.tsx - Filtered symbols:', filtered.map(f => f.symbol));
    
    // Filter out entries with invalid prices (0 or undefined)
    filtered = filtered.filter(data => 
      data.price !== undefined && 
      data.price !== null && 
      !isNaN(data.price) && 
      data.price > 0
    );
    console.log('Markets.tsx - After price filter, filtered length:', filtered.length);
    console.log('Markets.tsx - Final filtered symbols with prices:', filtered.map(f => ({ symbol: f.symbol, price: f.price })));
    
    // Create a map of market data for quick lookup
    const marketDataMap = new Map(filtered.map(data => [data.symbol, data]));
    console.log('Markets.tsx - marketDataMap keys:', Array.from(marketDataMap.keys()));
    
    // Ensure we show all predefined instruments, with real data if available
    filtered = [];
    
    // Add all predefined instruments, with real data if available
    predefinedList.forEach(item => {
      const existingData = marketDataMap.get(item.symbol);
      if (existingData) {
        filtered.push(existingData);
        console.log(`Markets.tsx - Found data for ${item.symbol}:`, existingData.price);
      } else {
        // Skip items that don't have real market data
        console.warn(`No market data found for ${item.symbol}, skipping`);
      }
    });
    
    // Update prices from WebSocket tickers if available
    // All prices come from marketData (Supabase database)
    
    // Sort favorites to the top, then by predefined order
    filtered.sort((a, b) => {
      const aIsFavorite = isFavorite(a.symbol);
      const bIsFavorite = isFavorite(b.symbol);
      
      // If one is favorite and the other isn't, favorite goes first
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      
      // If both are favorites or both are not favorites, sort by predefined order
      return sortByPredefinedOrder(a, b);
    });
    
    // Filter by search term if provided
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(data => 
        data.symbol.toLowerCase().includes(term) ||
        formatPairDisplay(data.symbol).toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  // Helper function to sort by predefined order
  const sortByPredefinedOrder = (a: MarketData, b: MarketData) => {
    if (tradingMode === 'cfd') {
      // For CFD mode, sort by type and then by predefined order
      const typeOrder = { 'forex': 1, 'commodity': 2, 'stock': 3 };
      const aInstrument = CFD_INSTRUMENTS.find(i => i.symbol === a.symbol);
      const bInstrument = CFD_INSTRUMENTS.find(i => i.symbol === b.symbol);
      
      if (aInstrument && bInstrument) {
        // First sort by type
        const typeComparison = typeOrder[aInstrument.type] - typeOrder[bInstrument.type];
        if (typeComparison !== 0) return typeComparison;
        
        // Then by predefined order
        return CFD_INSTRUMENTS.indexOf(aInstrument) - CFD_INSTRUMENTS.indexOf(bInstrument);
      }
      
      return a.symbol.localeCompare(b.symbol);
    } else {
      // For crypto mode, sort by predefined order (which is already ordered by market cap)
      const aIndex = TOP_CRYPTO_PAIRS.findIndex(pair => pair.symbol === a.symbol);
      const bIndex = TOP_CRYPTO_PAIRS.findIndex(pair => pair.symbol === b.symbol);
      
      // If both are in the predefined list, sort by their order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only one is in the predefined list, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      // If neither is in the predefined list, sort alphabetically
      return a.symbol.localeCompare(b.symbol);
    }
  };

  const filteredMarketData = getFilteredMarketData();

  return (
    <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700/50 shadow-2xl h-full flex flex-col" translate="no">
      {/* Markets Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-cyan-400 font-semibold text-lg">
          {tradingMode === 'cfd' ? t('cfd.title') : t('sidebar.marketOverview')}
        </h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <input 
            type="text" 
            placeholder={`${t('common.search')}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-slate-900/50 text-white text-sm pl-10 pr-4 py-2 rounded-xl w-36 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 border border-slate-600/50"
          />
        </div>
      </div>

      {/* Markets List */}
      <div className="flex-1 space-y-2 overflow-y-auto hide-scrollbar" translate="no">
        {filteredMarketData.map((market, index) => {
          // Get the instrument type for CFD mode
          let instrumentType = 'crypto';
          if (tradingMode === 'cfd') {
            const instrument = CFD_INSTRUMENTS.find(i => i.symbol === market.symbol);
            instrumentType = instrument?.type || 'unknown';
          }
          
          // Add category headers for CFD mode
          const showHeader = tradingMode === 'cfd' && 
            (index === 0 || 
             (index > 0 && 
              CFD_INSTRUMENTS.find(i => i.symbol === market.symbol)?.type !== 
              CFD_INSTRUMENTS.find(i => i.symbol === filteredMarketData[index-1].symbol)?.type));
          
          // Get real-time price from tickers if available
          const price = market.price;
          
          // Get change percentage from ticker or market data
          const change24h = market.change_24h || 0;

          // Determine price precision based on instrument type
          const getPricePrecision = (symbol: string): number => {
            // For CFD trading mode, use higher precision for forex pairs
            if (tradingMode === 'cfd') {
              // Forex pairs (6 characters, no USDT suffix)
              if (symbol.length === 6 && !symbol.includes('USDT')) {
                return 4; // 4 decimal places for forex
              }
              // Commodities (XAU, XAG, etc.)
              if (symbol.startsWith('XAU') || symbol.startsWith('XAG') || symbol.startsWith('XPT') || 
                  symbol.startsWith('XPD') || symbol === 'WTIUSD' || symbol === 'NGUSD') {
                return 2; // 2 decimal places for precious metals and commodities
              }
              // Stocks
              const stockSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NFLX', 'NVDA', 'BA', 'JPM'];
              if (stockSymbols.includes(symbol)) {
                return 2; // 2 decimal places for stocks
              }
            }
            
            // Default for crypto and other instruments
            return 2; // 2 decimal places for crypto
          };

          return (
            <React.Fragment key={index}>
              {showHeader && (
                <div className="text-xs font-semibold text-slate-400 uppercase mt-4 mb-2 pl-2">
                  {instrumentType === 'forex' ? t('cfd.forexPairs') : 
                   instrumentType === 'commodity' ? t('cfd.commodities') : 
                   instrumentType === 'stock' ? t('cfd.stocks') : ''}
                </div>
              )}
              <div 
                onClick={() => setSelectedPair(market.symbol)}
                className={`flex items-center justify-between hover:bg-slate-800/50 px-3 py-2 rounded-xl cursor-pointer transition-all duration-300 ${
                  selectedPair === market.symbol ? 'bg-slate-800/50 border-l-2 border-cyan-400 shadow-lg shadow-cyan-400/10' : 'border border-transparent hover:border-slate-600/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Star 
                    size={14} 
                    className={`transition-colors cursor-pointer ${
                      isFavorite(market.symbol) 
                        ? 'text-amber-400 hover:text-amber-300' 
                        : 'text-slate-600 hover:text-amber-400'
                    }`}
                    fill={isFavorite(market.symbol) ? 'currentColor' : 'none'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(market.symbol);
                    }}
                  />
                  <span className="text-slate-300 text-sm font-medium">{formatPairDisplay(market.symbol)}</span>
                </div>
                <span className={`text-slate-300 text-sm font-mono`}>
                  {price > 0 ? price.toFixed(getPricePrecision(market.symbol)) : '-'}
                </span>
                <div className="flex items-center gap-2">
                  {change24h >= 0 ? (
                    <TrendingUp size={14} className="text-emerald-400" />
                  ) : (
                    <TrendingDown size={14} className="text-red-400" />
                  )}
                  <span className={`text-sm font-medium ${change24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {change24h >= 0 ? '+' : ''}{change24h.toFixed(getInstrumentType(market.symbol) === 'forex' ? 4 : 2)}%
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        
        {filteredMarketData.length === 0 && (
          <div className="text-center text-slate-500 py-12">
            <p>{t('common.search')} - No results found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Markets;